import { DOM, state } from './state.js';
import { CONFIG } from './config.js';
import { SectionHeader } from './components/shared/SectionHeader.js';
import { ProductCard, ProductVarietyRow } from './components/menu/ProductCard.js';
import { PriceGroupCard } from './components/menu/PriceGroupCard.js';
import { CategoryNote } from './components/menu/CategoryNote.js';
import { hideDecorativeIcons } from './utils/aria.js';

/**
 * Renderiza la navegación principal en la página de inicio.
 */
export function renderHomeNavigation() {
    const navItems = [
        { id: 'carta', name: 'Nuestra Carta', path: '/carta', icon: 'fa-book-open', desc: 'Brunch, Almuerzo y Bar' },
        { id: 'reservas', name: 'Reservas', path: '/reservas', icon: 'fa-calendar-check', desc: 'Asegurá tu lugar' },
        { id: 'eventos', name: 'Eventos', path: '/eventos', icon: 'fa-cake-candles', desc: 'Tus momentos especiales' }
    ];

    DOM.mainNavigation.innerHTML = navItems.map(item => `
        <button data-action="navigate" data-path="${item.path}" 
            class="w-full p-6 rounded-3xl bg-surface border border-border-subtle flex items-center gap-6 transition-all active:scale-95 group">
            <div class="w-14 h-14 rounded-2xl bg-base border border-border-subtle flex items-center justify-center shadow-sm text-brand group-hover:bg-brand group-hover:text-white transition-colors">
                <i class="fa-solid ${item.icon} text-2xl"></i>
            </div>
            <div class="text-left">
                <span class="block text-[10px] font-black text-text-muted uppercase tracking-[0.2em] mb-1">${item.desc}</span>
                <span class="text-sm font-black text-text-primary uppercase tracking-tight">${item.name}</span>
            </div>
            <i class="fa-solid fa-chevron-right ml-auto text-text-muted"></i>
        </button>
    `).join('');
}

/**
 * Selección de macro-secciones en la vista de carta.
 */
export function renderMenuSelection() {
    DOM.menuSelectionContainer.innerHTML = CONFIG.SECCIONES_CARTA.map(section => `
        <button data-action="navigate" data-path="/carta/${section.id}" 
            class="w-full p-6 rounded-3xl bg-base border border-border-subtle flex items-center gap-6 transition-all active:scale-95 group shadow-sm">
            <div class="w-14 h-14 rounded-2xl bg-surface flex items-center justify-center text-brand group-hover:bg-brand group-hover:text-white transition-colors">
                <i class="fa-solid ${section.icon} text-2xl"></i>
            </div>
            <div class="text-left">
                <span class="block text-[10px] font-black text-text-muted uppercase tracking-[0.2em] mb-1">Explorar</span>
                <span class="text-sm font-black text-text-primary uppercase tracking-tight">${section.name}</span>
            </div>
            <i class="fa-solid fa-chevron-right ml-auto text-text-muted"></i>
        </button>
    `).join('');
}

/**
 * Botones de navegación en el sidebar y vistas según contexto.
 */
export function renderMajorSectionsUI() {
    const path = window.location.pathname;
    const parts = [];

    if (path.startsWith('/carta/')) {
        parts.push(`
            <button data-action="navigate-and-close" data-path="/" 
                class="w-full p-4 mb-4 rounded-2xl flex items-center gap-4 bg-brand text-white shadow-xl">
                <div class="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <i class="fa-solid fa-arrow-left-long text-lg"></i>
                </div>
                <span class="text-xs font-black uppercase tracking-tight">Volver al Inicio</span>
            </button>
        `);
        parts.push(`<p class="px-4 py-2 text-[10px] font-black text-text-muted uppercase tracking-widest">Nuestra Carta</p>`);

        CONFIG.SECCIONES_CARTA.forEach(section => {
            const fullPath = `/carta/${section.id}`;
            const isActive = path === fullPath;
            parts.push(`
                <button data-action="navigate-and-close" data-path="${fullPath}" 
                    class="w-full p-4 rounded-2xl flex items-center gap-4 transition-all ${isActive ? 'bg-invert-bg text-invert-text shadow-lg' : 'bg-surface text-text-muted'}">
                    <div class="w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-base border border-border-subtle shadow-sm text-brand'}">
                        <i class="fa-solid ${section.icon} text-lg"></i>
                    </div>
                    <span class="text-xs font-black uppercase tracking-tight">${section.name}</span>
                </button>
            `);
        });
    } else {
        const rootPaths = [
            { id: 'home', name: 'Inicio', path: '/', icon: 'fa-house' },
            { id: 'carta', name: 'Nuestra Carta', path: '/carta', icon: 'fa-book-open' },
            { id: 'reservas', name: 'Reservas', path: '/reservas', icon: 'fa-calendar-check' },
            { id: 'eventos', name: 'Eventos', path: '/eventos', icon: 'fa-cake-candles' }
        ];

        rootPaths.forEach(item => {
            const isActive = path === item.path;
            parts.push(`
                <button data-action="navigate-and-close" data-path="${item.path}" 
                    class="w-full p-4 rounded-2xl flex items-center gap-4 transition-all ${isActive ? 'bg-brand text-white shadow-lg' : 'bg-surface text-text-muted'}">
                    <div class="w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-base border border-border-subtle shadow-sm text-brand'}">
                        <i class="fa-solid ${item.icon} text-lg"></i>
                    </div>
                    <span class="text-xs font-black uppercase tracking-tight">${item.name}</span>
                </button>
            `);
        });
    }

    DOM.sidebarButtons.innerHTML = parts.join('');
    hideDecorativeIcons(DOM.sidebarButtons);
}

/**
 * Inyecta los skeletons de carga.
 */
export function renderLoadingSkeleton() {
    DOM.products.innerHTML = CONFIG.SECCIONES_CARTA.map(() => `
        <div class="macro-section-slide opacity-40">
            <div class="h-4 w-24 shimmer rounded-md mb-8"></div>
            <div class="product-grid">
                ${Array(4).fill(0).map(() => `
                    <div class="py-4 border-b border-border-subtle">
                        <div class="h-4 w-3/4 shimmer rounded-md mb-2"></div>
                        <div class="h-3 w-1/2 shimmer rounded-md mb-2"></div>
                        <div class="h-4 w-1/3 shimmer rounded-md"></div>
                    </div>
                `).join('')}
            </div>
        </div>
    `).join('');
}

/**
 * Renderizado de productos y estructura de secciones de menú con optimización de DOM.
 */
/**
 * Agrupa productos por variedad (Media / Entera) para la categoría de Pizzas.
 * Las medias vienen de Fudo con el prefijo "MEDIA " (ej. "MEDIA MUZZARELLA") y se
 * agrupan con su entera del mismo nombre base; su precio va a la columna Media.
 * (El naming de Fudo es "MEDIA ", no "1/2 " como se asumía antes.) Exportada para test.
 */
export function groupPizzaVarieties(products) {
    const varieties = {};
    
    products.forEach(p => {
        const half = /^media\s+/i.exec(p.name);
        const isHalf = half !== null;
        const baseName = isHalf ? p.name.slice(half[0].length).trim() : p.name.trim();
        
        if (!varieties[baseName]) {
            varieties[baseName] = { name: baseName, entera: null, media: null, description: p.description };
        }
        
        if (isHalf) {
            varieties[baseName].media = p;
        } else {
            varieties[baseName].entera = p;
            if (p.description) varieties[baseName].description = p.description;
        }
    });
    
    return Object.values(varieties);
}

/**
 * Renderizado de productos y estructura de secciones de menú con optimización de DOM.
 */
let lastRenderedProducts = null;

export function renderProductsUI() {
    if (state.loading) { renderLoadingSkeleton(); return; }
    
    // Optimización: Si los productos no cambiaron, no reconstruir el DOM
    if (lastRenderedProducts === state.products && DOM.products.innerHTML !== '') {
        return;
    }
    
    lastRenderedProducts = state.products;
    
    // Optimización: Si los productos no cambiaron y YA hay contenido real (no skeletons)
    const isAlreadyRendered = DOM.products.querySelector('.macro-section-slide:not(.opacity-40)');
    if (lastRenderedProducts === state.products && isAlreadyRendered) {
        return;
    }
    
    // Evitar colapso de altura durante el re-render (Layout Stability)
    const currentHeight = DOM.products.offsetHeight;
    if (currentHeight > 0) {
        DOM.products.style.minHeight = `${currentHeight}px`;
    }
    
    DOM.products.innerHTML = '';
    
    const fragment = document.createDocumentFragment();

    CONFIG.SECCIONES_CARTA.forEach(section => {
        const slide = document.createElement('div');
        slide.id = `slide-${section.id}`;
        slide.className = "macro-section-slide";
        
        const sectionProducts = state.productsBySection.get(section.id) || [];
        const sectionCategoryIds = new Set(sectionProducts.map(p => p.productCategoryId));
        const sectionCategories = state.categories.filter(c => sectionCategoryIds.has(c.id))
                                   .sort((a, b) => section.categoryIds.indexOf(Number(a.id)) - section.categoryIds.indexOf(Number(b.id)));

        if (sectionProducts.length === 0) {
            slide.innerHTML = `<div class="text-center py-20 bg-surface rounded-3xl border border-dashed border-border-default m-6">No hay productos disponibles</div>`;
        } else {
            const slideParts = [];

            sectionCategories.forEach(cat => {
                const productsInCat = sectionProducts.filter(p => p.productCategoryId === cat.id);
                if (productsInCat.length === 0) return;

                const catId = String(cat.id);

                // --- SALSAS (150) / GUARNICIONES (149): tarjeta condensada,
                //     agrupada por precio. NO usa el SectionHeader estándar — la
                //     tarjeta trae su propio título adentro. En salsas se omite la
                //     palabra "salsa" de cada nombre (la categoría ya lo dice). ---
                if (catId === '150' || catId === '149') {
                    slideParts.push(PriceGroupCard({
                        title: cat.name,
                        products: productsInCat,
                        stripWord: catId === '150' ? 'salsa' : null,
                    }));
                    return;
                }

                // Header de Categoría
                slideParts.push(SectionHeader({ title: cat.name }));

                // Aclaración de la categoría (ej. leche vegetal en cafetería),
                // derivada de los grupos de modificadores de Fudo.
                const note = state.categoryNotes?.get(Number(cat.id));
                if (note) slideParts.push(CategoryNote(note));

                // --- LÓGICA ESPECIAL PARA PIZZAS (ID 124) ---
                if (catId === '124') {
                    // Orden Fudo: cada variedad hereda la `position` de su entera
                    // (o la media, si sólo hay media). Ver menu.service.init.
                    const pizzaPos = (v) => (v.entera?.position ?? v.media?.position ?? 0);
                    const grouped = groupPizzaVarieties(productsInCat).sort((a, b) => pizzaPos(a) - pizzaPos(b));
                    slideParts.push(`
                        <div class="w-full mb-8">
                            <div class="flex items-center border-b-2 border-border-subtle pb-2 mb-2 px-1">
                                <div class="flex-1 text-[9px] font-black text-text-muted uppercase tracking-widest">Variedad</div>
                                <div class="w-16 text-center flex flex-col items-center">
                                    <i class="fa-solid fa-circle-half-stroke text-[11px] text-brand opacity-60"></i>
                                    <span class="text-[8px] font-black text-text-muted uppercase mt-1">1/2</span>
                                </div>
                                <div class="w-16 text-center flex flex-col items-center">
                                    <i class="fa-solid fa-circle text-[11px] text-brand"></i>
                                    <span class="text-[8px] font-black text-text-muted uppercase mt-1">Entera</span>
                                </div>
                            </div>
                            ${grouped.map(item => ProductVarietyRow({
                                name: item.name,
                                description: item.description,
                                priceMedia: item.media ? item.media.price : '-',
                                priceEntera: item.entera ? item.entera.price : '-'
                            })).join('')}
                        </div>`);
                } else {
                    // Grid de productos estándar, ordenado por `position` (el
                    // orden que el staff define en Fudo), no alfabético.
                    const sortedProducts = [...productsInCat].sort((a, b) => a.position - b.position);
                    // Flujo COLUMN-MAJOR: se llena la columna izquierda entera
                    // (de arriba a abajo) y recién después la derecha. Fijamos
                    // las filas en ceil(n/2) y `grid-auto-flow: column`, así el
                    // corte es por CANTIDAD (mitad izq / mitad der), no por
                    // altura. La izquierda se queda con el sobrante en n impar.
                    const gridRows = Math.ceil(sortedProducts.length / 2);
                    slideParts.push(`<div class="product-grid" style="grid-auto-flow: column; grid-template-rows: repeat(${gridRows}, auto);">
                        ${sortedProducts.map(p => ProductCard(p)).join('')}
                    </div>`);
                }
            });
            slide.innerHTML = slideParts.join('');
        }
        fragment.appendChild(slide);
    });

    DOM.products.appendChild(fragment);
    DOM.products.style.minHeight = '';
    hideDecorativeIcons(DOM.products);
}

export function renderMacroTabs() {
    DOM.macroTabsContainer.innerHTML = CONFIG.SECCIONES_CARTA.map(section => {
        const isActive = state.activeMajorSectionId === section.id;
        return `<button data-action="navigate" data-path="/carta/${section.id}" class="macro-tab ${isActive ? 'active' : ''}">${section.name}</button>`;
    }).join('');
}

